import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import SettingsIcon from "@mui/icons-material/Settings";
import { useSelector } from "react-redux";
import {
  AppBar,
  Box,
  Container,
  Divider,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
} from "@mui/material";
import { pages } from "../Admin/pages";
import { selectAllNavBadges } from "../../redux/features/navSelectors";

const NavMenu = ({ currentOrganization }) => {
  const navBadges = useSelector(selectAllNavBadges) ?? {};
  if (!currentOrganization) {
    return null;
  }
  return (
    <Box
      sx={{
        display: "flex",
      }}
    >
      {pages({ currentOrganization }).map((page) => {
        const badgeCount = Number(navBadges[page.key]) || 0;
        const showBadge = badgeCount > 0;
        return (
          <Button
            component={Link}
            key={page.key}
            to={page.route}
            selected={true}
            sx={{ my: 0, color: "white", display: "inline-flex", alignItems: "center", gap: 0.5 }}
          >
            {page.name}
            {showBadge && (
              <Box
                component="span"
                sx={{
                  bgcolor: "error.main",
                  color: "white",
                  borderRadius: "10px",
                  px: 0.5,
                  py: 0,
                  minWidth: 18,
                  height: 18,
                  fontSize: "0.75rem",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {badgeCount > 99 ? "99+" : badgeCount}
              </Box>
            )}
          </Button>
        );
      })}
    </Box>
  );
};

export const CRM_ORG_ID = "__CRM__";
export const DEV_TOOLS_ORG_ID = "__DEV_TOOLS__";

const OrganizationSelector = ({
  user,
  organizations,
  invitations,
  ownershipTransfers = [],
  currentOrganization,
  handleChangeOrganization,
  handleClickNewOrganization,
  handleClickInvitation,
  handleClickOwnershipTransfer,
}) => {
  const location = useLocation();
  const [anchorEOrg, setAnchorElOrg] = React.useState(null);

  const handleCloseUserMenu = () => {
    setAnchorElOrg(null);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElOrg(event.currentTarget);
  };

  if (!organizations || !invitations) {
    return;
  }

  const isInCRM = location.pathname.startsWith("/crm");
  const isInDevTools = location.pathname.startsWith("/dev-tools");
  const hasOrganizations = organizations.length > 0;
  const isSuperAdmin = user?.superAdmin;
  const isDeveloper = user?.developer === true;

  let menuText;
  if (isInCRM) {
    menuText = "CRM";
  } else if (isInDevTools) {
    menuText = "Dev Tools";
  } else if (currentOrganization) {
    menuText = currentOrganization.name;
  } else {
    menuText = "Organizations";
  }

  return (
    <Box>
      <Tooltip title="Organizations">
        <Button
          onClick={handleOpenUserMenu}
          sx={{ my: 2, color: "white", display: "block" }}
        >
          <Typography textTransform={"initial"}>{menuText}</Typography>
        </Button>
      </Tooltip>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-org-appbar"
        anchorEl={anchorEOrg}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorEOrg)}
        onClose={handleCloseUserMenu}
      >
        {isSuperAdmin &&
          [
            <MenuItem
              selected={isInCRM}
              key={`org-selector-${CRM_ORG_ID}`}
              onClick={(e) => {
                handleCloseUserMenu();
                handleChangeOrganization(CRM_ORG_ID);
              }}
            >
              <Typography>CRM</Typography>
            </MenuItem>,
            hasOrganizations ? <Divider key="crm-divider" /> : null,
          ].filter(Boolean)}
        {isDeveloper &&
          [
            <MenuItem
              selected={isInDevTools}
              key={`org-selector-${DEV_TOOLS_ORG_ID}`}
              onClick={(e) => {
                handleCloseUserMenu();
                handleChangeOrganization(DEV_TOOLS_ORG_ID);
              }}
            >
              <Typography>Dev Tools</Typography>
            </MenuItem>,
            hasOrganizations ? <Divider key="dev-tools-divider" /> : null,
          ].filter(Boolean)}
        {hasOrganizations && (
          <Box padding={"20px"}>
            <Typography>Organizations</Typography>
          </Box>
        )}

        {organizations
          .filter(({ id }) => Boolean(id))
          .map(({ id, name, userIsOwner, userIsAdmin }) => {
            const canAccessSettings = userIsOwner || userIsAdmin;
            return (
              <MenuItem
                selected={id === currentOrganization?.id}
                key={`org-selector-${id}`}
                onClick={(e) => {
                  handleCloseUserMenu();
                  handleChangeOrganization(id);
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  <Typography>{name}</Typography>
                  {canAccessSettings && (
                    <Tooltip title="Organization settings">
                      <IconButton
                        component={Link}
                        to={`/organizations/${id}`}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseUserMenu();
                        }}
                        aria-label={`${name} settings`}
                      >
                        <SettingsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        {hasOrganizations && <Divider />}
        <MenuItem onClick={handleClickNewOrganization}>
          <Typography>New Organization</Typography>
        </MenuItem>
        {invitations.length > 0 && <Divider />}
        {invitations.length > 0 && (
          <Box padding={"20px"}>
            <Typography>Invitations</Typography>
          </Box>
        )}
        {invitations.map(({ id, organizationName }) => {
          return (
            <MenuItem
              key={`org-selector-${id}`}
              onClick={(e) => {
                handleCloseUserMenu();
                handleClickInvitation(id);
              }}
            >
              <Typography>{organizationName}</Typography>
            </MenuItem>
          );
        })}
        {ownershipTransfers.length > 0 && <Divider />}
        {ownershipTransfers.length > 0 && (
          <Box padding={"20px"}>
            <Typography>Ownership transfers</Typography>
          </Box>
        )}
        {ownershipTransfers.map(({ id, organizationName }) => {
          return (
            <MenuItem
              key={`ownership-transfer-${id}`}
              onClick={() => {
                handleCloseUserMenu();
                handleClickOwnershipTransfer(id);
              }}
            >
              <Typography>{organizationName}</Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

const UserMenu = ({ user, handleLogoutClick }) => {
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const settings = [{ name: "Logout", clickHandler: handleLogoutClick }];

  const avatar = (user) => {
    const { displayName, photoURL } = user || {};
    return <Avatar alt={displayName} src={photoURL} />;
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  if (!user) {
    return null;
  }
  return (
    <Box sx={{ display: "flex", justifyContent: "right" }}>
      <Tooltip title="Open settings">
        <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
          {avatar(user)}
        </IconButton>
      </Tooltip>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(anchorElUser)}
        onClose={handleCloseUserMenu}
      >
        <Box display={"flex"} alignItems={"center"} margin={"20px"}>
          {avatar(user)}
          <Typography marginLeft={"10px"}>{user.displayName}</Typography>
        </Box>
        <Divider sx={{ marginBottom: "10px" }} />
        {settings.map(({ name, clickHandler }) => (
          <MenuItem
            key={name}
            onClick={() => {
              handleCloseUserMenu();
              clickHandler();
            }}
          >
            <Typography textAlign="center">{name}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const ResponsiveAppBar = ({
  handleLogoutClick,
  user,
  organizations,
  invitations,
  ownershipTransfers,
  currentOrganization,
  handleChangeOrganization,
  handleClickNewOrganization,
  handleClickInvitation,
  handleClickOwnershipTransfer,
}) => {
  const location = useLocation();
  const isInCRM = location.pathname.startsWith("/crm");
  const isInDevTools = location.pathname.startsWith("/dev-tools");

  return (
    <Box sx={{ flexGrow: 1, overflow: "visible" }}>
      <AppBar position="sticky" sx={{ overflow: "visible !important" }}>
        <Toolbar position="relative" sx={{ overflow: "visible" }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 0 }}>
            Comedy Ticketing
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "right",
              gap: "60px",
            }}
          >
            <OrganizationSelector
              user={user}
              invitations={invitations}
              ownershipTransfers={ownershipTransfers}
              organizations={organizations}
              currentOrganization={currentOrganization}
              handleChangeOrganization={handleChangeOrganization}
              handleClickNewOrganization={handleClickNewOrganization}
              handleClickInvitation={handleClickInvitation}
              handleClickOwnershipTransfer={handleClickOwnershipTransfer}
            />
            <UserMenu user={user} handleLogoutClick={handleLogoutClick} />
          </Box>
        </Toolbar>

        {currentOrganization && !isInCRM && !isInDevTools && (
          <Toolbar
            sx={{
              backgroundColor: "grey.500",
              minHeight: 56,
              overflow: "visible !important",
              pt: 1.25,
              pb: 0.5,
              boxSizing: "border-box",
            }}
          >
            <Container sx={{ width: "100%", maxWidth: "100%", px: 0, overflow: "visible" }}>
              <Box
                sx={{
                  width: "100%",
                  overflowX: "auto",
                  overflowY: "visible",
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  px: 1.5,
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <NavMenu currentOrganization={currentOrganization} />
              </Box>
            </Container>
          </Toolbar>
        )}
      </AppBar>
    </Box>
  );
};
export default ResponsiveAppBar;
